import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowUpRight, Loader2, CornerDownLeft, Send, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const SUGGESTIONS = [
    "What has Ana been chatting about on WhatsApp?",
    "Show me my £20k goal progress",
    "Which leads need chasing today?",
    "Show clients with expiring domains",
    "Ping me about Big White Duck's renewal",
];

const ROUTE_BY_TARGET = {
    clients: "/app/clients",
    tasks: "/app/tasks",
    leads: "/app/leads",
    goals: "/app/goals",
    dashboard: "/app",
    conversations: "/app/conversations",
};

export function CommandBar({ open, onClose }) {
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [waSent, setWaSent] = useState(false);
    const [waSending, setWaSending] = useState(false);
    const navigate = useNavigate();
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            setText("");
            setResult(null);
            setLoading(false);
            setWaSent(false);
            setWaSending(false);
        }
    }, [open]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const submit = async (raw) => {
        const q = (raw ?? text).trim();
        if (!q) return;
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post("/ai/command", { text: q });
            setResult(data);
            if (data.intent === "navigate" && data.target && ROUTE_BY_TARGET[data.target]) {
                setTimeout(() => {
                    navigate(ROUTE_BY_TARGET[data.target]);
                    onClose();
                }, 600);
            }
        } catch (e) {
            setResult({ answer: formatApiErrorDetail(e.response?.data?.detail) || e.message, intent: "answer" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    data-testid="command-bar-overlay"
                >
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

                    <motion.div
                        className="relative w-full max-w-2xl rounded-2xl border border-white/15 bg-[#0A0A0C]/90 backdrop-blur-3xl shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] elate-ai-border"
                        initial={{ y: -16, opacity: 0, scale: 0.98 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: -8, opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.06]">
                            <Sparkles className="h-4 w-4 text-[#0066FF]" strokeWidth={1.7} />
                            <input
                                ref={inputRef}
                                data-testid="command-bar-input"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") submit();
                                }}
                                placeholder="Ask Ana anything…"
                                className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-zinc-500"
                            />
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                            ) : (
                                <kbd className="font-mono text-[10px] text-zinc-500">esc</kbd>
                            )}
                        </div>

                        <div className="p-3 max-h-[60vh] overflow-y-auto">
                            {!result && !loading && (
                                <div className="space-y-1">
                                    <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                                        Suggestions
                                    </div>
                                    {SUGGESTIONS.map((s) => (
                                        <button
                                            key={s}
                                            data-testid={`command-suggestion`}
                                            onClick={() => {
                                                setText(s);
                                                submit(s);
                                            }}
                                            className="w-full text-left px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:bg-white/[0.04] hover:text-white transition flex items-center justify-between gap-3"
                                        >
                                            <span>{s}</span>
                                            <CornerDownLeft className="h-3.5 w-3.5 text-zinc-600" />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {loading && (
                                <div className="px-3 py-6 text-sm text-zinc-400 flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full bg-[#0066FF] elate-breathe" />
                                    Ana is thinking…
                                </div>
                            )}

                            {result && !loading && (
                                <div className="px-3 py-3 space-y-3">
                                    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                                            Ana
                                        </div>
                                        <div className="text-sm text-zinc-100 leading-relaxed" data-testid="command-bar-answer">
                                            {result.answer}
                                        </div>
                                    </div>
                                    {result.target && ROUTE_BY_TARGET[result.target] && (
                                        <button
                                            data-testid="command-bar-go-target"
                                            onClick={() => {
                                                navigate(ROUTE_BY_TARGET[result.target]);
                                                onClose();
                                            }}
                                            className="w-full flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm hover:bg-white/[0.05] transition"
                                        >
                                            <span>
                                                Open <span className="capitalize text-white">{result.target}</span>
                                                {result.filter ? ` · ${result.filter}` : ""}
                                            </span>
                                            <ArrowUpRight className="h-4 w-4 text-zinc-400" />
                                        </button>
                                    )}
                                    {result.intent === "message_client" && (
                                        <button
                                            data-testid="command-bar-send-whatsapp"
                                            disabled={waSending || waSent}
                                            onClick={async () => {
                                                setWaSending(true);
                                                try {
                                                    await api.post("/ana/outbox", {
                                                        client_hint: result.filter || null,
                                                        message: result.whatsapp_message || result.answer,
                                                        reason: text,
                                                    });
                                                    setWaSent(true);
                                                } finally {
                                                    setWaSending(false);
                                                }
                                            }}
                                            className="w-full flex items-center justify-between rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm hover:bg-emerald-500/15 transition disabled:opacity-60"
                                        >
                                            <span className="flex items-center gap-2 text-emerald-200">
                                                {waSent ? (
                                                    <><Check className="h-4 w-4" /> Queued — Ana will ping you</>
                                                ) : (
                                                    <><Send className="h-4 w-4" /> Ping me on WhatsApp</>
                                                )}
                                            </span>
                                            {waSending && <Loader2 className="h-4 w-4 animate-spin text-emerald-300" />}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="border-t border-white/[0.06] px-4 py-2 flex items-center justify-between text-[11px] text-zinc-500">
                            <span className="font-mono">claude-sonnet-4.5</span>
                            <span>↵ to ask · esc to close</span>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
